<template>
  <b-card
    no-body
  >
    <b-card-header>
      <b-card-title>{{ $t('Account Stats By Status') }}</b-card-title>
      <feather-icon
        icon="RotateCwIcon"
        class="cursor-pointer"
        @click="fetchAccountStatus"
      />
    </b-card-header>

    <vue-apex-charts
      type="pie"
      height="325"
      class="mt-2 mb-1"
      :options="pieChartOptions.chartOptions"
      :series="pieChartOptions.series"
    />

    <!-- list group -->
    <div class="p-2">
      <div
        v-for="(item,index) in listData"
        :key="index"
        class="d-flex justify-content-between"
        :class="index === Object.keys(listData).length - 1 ? '':'mb-1'"
      >
        <div class="series-info">
          <feather-icon
            :icon="item.icon"
            size="16"
            class="mr-50"
            :class="item.iconColor"
          />
          <span class="font-weight-bolder">{{ item.text }}</span>
        </div>
        <span>{{ item.result }}</span>
      </div>
    </div>

    <Spinner :show="isLoadingAccountStatus" />
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { onMounted, computed, ref } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import Spinner from '@/views/components/spinner/Spinner.vue'
import useToastAlert from '../../../composable/useToast'
import toNumber from '@/helpers/toNumber'
import { getIconColor, getChartColor } from '@/helpers/getColors'

export default {
  components: {
    VueApexCharts,
    BCard,
    BCardHeader,
    BCardTitle,
    Spinner,
  },
  setup() {
    const { getAccountsByStatus } = useActions({ getAccountsByStatus: 'dashboard/getAccountsByStatus' })
    const { toastAlert } = useToastAlert()
    const { t } = useI18nUtils()

    const accountData = ref([])
    const isLoadingAccountStatus = ref(false)

    // Computed for labels
    const labels = computed(() => accountData.value.map(item => t(item.label)))

    // Computed for chart series
    const accountStat = computed(() => accountData.value.map(item => item.totalCount))

    // Computed for chart colors
    const pieChartColors = computed(() => accountData.value.map(item => getChartColor(item.label.trim())))

    // Computed for list data
    const listData = computed(() => accountData.value.map(item => ({
      icon: 'StopCircleIcon',
      iconColor: getIconColor(item.label.trim()),
      text: t(item.label),
      result: toNumber(item.totalCount),
    })))

    // Pie chart options using computed values
    const pieChartOptions = computed(() => ({
      series: accountStat.value,
      chartOptions: {
        chart: {
          toolbar: {
            show: false,
          },
        },
        labels: labels.value,
        dataLabels: {
          enabled: true,
        },
        legend: { show: false },
        stroke: {
          width: 4,
        },
        colors: pieChartColors.value,
      },
    }))

    const fetchAccountStatus = async () => {
      try {
        isLoadingAccountStatus.value = true
        const { status, data } = await getAccountsByStatus()

        if (status === 200 && data) {
          accountData.value = data
        } else {
          accountData.value = [] // Fallback if no data is returned
        }
      } catch (err) {
        toastAlert(err)
      } finally {
        isLoadingAccountStatus.value = false
      }
    }

    onMounted(fetchAccountStatus)

    return {
      isLoadingAccountStatus,
      pieChartOptions,
      listData,
      fetchAccountStatus,
    }
  },
}
</script>
